import { useMemo } from "react";
import {
  AssessmentQuestionResponseVisibility,
  AssessmentType,
  CalibrationNodeFragment,
  MostRecentAssessmentAnswerNode,
} from "types/graphql-schema";
import { BasicUser } from "types/topicflow";

import useLabel from "@apps/use-label/use-label";

import AssessmentQuestionHeading from "../questions/assessment-question-heading";
import { getQuestionClass } from "../questions/question";
import {
  AssessmentAnswerWithResponderAndId,
  AssessmentQuestion,
} from "../questions/types";
import AssessmentDeliveryResponderCheckbox from "./assessment-delivery-responder-checkbox";

const AssessmentDeliveryQuestion = ({
  canUpdate,
  assessmentType,
  excludedAnswerIds,
  answers,
  mostRecentAnswers,
  question,
  calibrations,
  target,
  periodEndDate,
  periodStartDate,
  onUpdateExcludedAnswers,
}: {
  canUpdate: boolean;
  excludedAnswerIds: number[];
  answers: AssessmentAnswerWithResponderAndId[];
  mostRecentAnswers?: Pick<
    MostRecentAssessmentAnswerNode,
    "integerAnswer" | "questionId" | "submitted"
  >[];
  question: AssessmentQuestion;
  calibrations?: CalibrationNodeFragment[];
  assessmentType?: AssessmentType;
  target?: BasicUser | null;
  periodStartDate?: string | null;
  periodEndDate?: string | null;
  onUpdateExcludedAnswers: (excludedAnswerIds: number[]) => void;
}) => {
  const label = useLabel();
  const QuestionClass = getQuestionClass(question, { label });
  const mostRecentAnswer = useMemo(
    () =>
      mostRecentAnswers
        ? mostRecentAnswers.find((a) => a.questionId === question.id)
        : undefined,
    [mostRecentAnswers, question],
  );
  const currentAnswer = useMemo(() => {
    const managerAnswer = answers.find(
      (ans) => ans.responder?.id !== target?.id && ans.integerAnswer,
    );
    return managerAnswer?.integerAnswer
      ? managerAnswer.integerAnswer
      : undefined;
  }, [answers, target]);

  if (!canUpdate && answers.length === 0) {
    return null;
  }

  const hiddenFromSubject =
    question.responseVisibility ===
    AssessmentQuestionResponseVisibility.HiddenFromSubject;

  return (
    <div
      key={question.id}
      id={`assessment-question-${question.id}`}
      className="border rounded-lg"
      aria-label={`Assessment question: ${question.title}`}
    >
      <div className="px-6 pt-6">
        <AssessmentQuestionHeading
          question={question}
          target={target}
          mostRecentAnswer={mostRecentAnswer}
          assessmentType={assessmentType}
          currentAnswer={currentAnswer}
        />
      </div>

      <div className="flex flex-col gap-4 mt-4">
        {canUpdate ? (
          answers?.map((answer) => {
            const isExcluded = excludedAnswerIds.includes(answer.id);

            const responder = (
              <AssessmentDeliveryResponderCheckbox
                hiddenFromSubject={hiddenFromSubject}
                isExcluded={isExcluded}
                canUpdate={canUpdate}
                answer={answer}
                target={target}
                excludedAnswerIds={excludedAnswerIds}
                onUpdateExcludedAnswers={onUpdateExcludedAnswers}
              />
            );

            if (isExcluded) {
              return (
                <div key={answer.id} className="px-6 pb-6">
                  {responder}
                </div>
              );
            }

            return (
              <div key={answer.id}>
                <div className="px-6">{responder}</div>
                {QuestionClass.getDelivery({
                  answer,
                  calibrations,
                  assessmentType,
                })}
              </div>
            );
          })
        ) : (
          <div>
            {QuestionClass.getDeliveryReadonly({
              answers,
              calibrations,
              target,
              periodEndDate,
              periodStartDate,
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AssessmentDeliveryQuestion;
